import React, { useCallback } from 'react';
import { FL151 } from '../../../services/l205Api';

interface Tab2Props {
  details: Partial<FL151>[];
  selectedRow: number;
  isEditing: boolean;
  setSelectedRow: (idx: number) => void;
  updateDetailRow: (idx: number, key: keyof FL151, value: FL151[keyof FL151]) => void;
  addDetailRow: () => void;
  deleteDetailRow: () => void;
}

const VERDICT_OPTIONS = [
  { value: '', label: '(未判定)' },
  { value: 'Y', label: 'Y 符合' },
  { value: 'N', label: 'N 不符合' },
];

const Tab2Details: React.FC<Tab2Props> = ({
  details, selectedRow, isEditing,
  setSelectedRow, updateDetailRow,
  addDetailRow, deleteDetailRow,
}) => {

  const handleCellChange = useCallback((idx: number, key: keyof FL151, value: string) => {
    updateDetailRow(idx, key, value as FL151[keyof FL151]);
  }, [updateDetailRow]);

  return (
    <div>
      {/* Toolbar */}
      <div className="table-wrapper">
        <div className="table-toolbar">
          <div className="left">
            <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.65)' }}>
              查核項目明細（共 {details.length} 項）
            </span>
          </div>
          <div className="right">
            <button
              className="btn btn-sm"
              disabled={!isEditing}
              onClick={addDetailRow}
            >
              明細新增
            </button>
            <button
              className="btn btn-sm btn-danger"
              disabled={!isEditing || details.length <= 1}
              onClick={deleteDetailRow}
            >
              明細刪除
            </button>
          </div>
        </div>

        {/* Detail Grid */}
        <div className="table-scroll" style={{ maxHeight: 400 }}>
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 50 }}>項次</th>
                <th style={{ width: 80 }}>章節</th>
                <th style={{ width: 240 }}>確認事項</th>
                <th style={{ width: 90 }}>判定</th>
                <th style={{ width: 240 }}>稽核記錄</th>
                <th style={{ width: 80 }}>受稽核人</th>
                <th style={{ width: 90 }}>姓名</th>
                <th style={{ width: 90 }}>部門</th>
                <th style={{ width: 60 }}>留言次數</th>
                <th style={{ width: 100 }}>最後留言日</th>
              </tr>
            </thead>
            <tbody>
              {details.map((d, idx) => (
                <tr
                  key={idx}
                  className={selectedRow === idx ? 'selected' : ''}
                  onClick={() => setSelectedRow(idx)}
                >
                  {/* 項次 - readonly */}
                  <td style={{ textAlign: 'center' }}>{d.fl151_02 ?? idx + 1}</td>

                  {/* 章節 */}
                  <td>
                    <input
                      type="text"
                      style={{ width: '100%', fontSize: 13 }}
                      maxLength={8}
                      disabled={!isEditing}
                      value={d.fl151_11 || ''}
                      onChange={e => handleCellChange(idx, 'fl151_11', e.target.value)}
                    />
                  </td>

                  {/* 確認事項 (editbox → textarea) */}
                  <td>
                    <textarea
                      style={{ width: '100%', fontSize: 13, minHeight: 50, resize: 'vertical' }}
                      disabled={!isEditing}
                      value={d.fl151_12 || ''}
                      onChange={e => handleCellChange(idx, 'fl151_12', e.target.value)}
                    />
                  </td>

                  {/* 判定 (optiongroup → select for grid space) */}
                  <td>
                    <select
                      style={{ width: '100%', fontSize: 13 }}
                      disabled={!isEditing}
                      value={d.fl151_21 || ''}
                      onChange={e => handleCellChange(idx, 'fl151_21', e.target.value)}
                    >
                      {VERDICT_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </td>

                  {/* 稽核記錄 (editbox → textarea) */}
                  <td>
                    <textarea
                      style={{ width: '100%', fontSize: 13, minHeight: 50, resize: 'vertical' }}
                      disabled={!isEditing}
                      value={d.fl151_22 || ''}
                      onChange={e => handleCellChange(idx, 'fl151_22', e.target.value)}
                    />
                  </td>

                  {/* 受稽核人 */}
                  <td>
                    <input
                      type="text"
                      style={{ width: '100%', fontSize: 13 }}
                      maxLength={8}
                      disabled={!isEditing}
                      value={d.fl151_03 || ''}
                      onChange={e => handleCellChange(idx, 'fl151_03', e.target.value)}
                    />
                  </td>

                  {/* 姓名 (readonly, auto-filled) */}
                  <td>
                    <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.65)' }}>
                      {d.fl151_03a || ''}
                    </span>
                  </td>

                  {/* 部門名稱 (readonly, auto-filled) */}
                  <td>
                    <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.65)' }}>
                      {d.fl151_04a || ''}
                    </span>
                  </td>

                  {/* 累計傳送留言次數 (readonly) */}
                  <td style={{ textAlign: 'center' }}>
                    <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.65)' }}>
                      {d.fl151_31 ?? 0}
                    </span>
                  </td>

                  {/* 最後傳送留言日期 (readonly) */}
                  <td>
                    <span style={{ fontSize: 12, color: 'rgba(0,0,0,0.65)' }}>
                      {d.fl151_32 ? String(d.fl151_32).substring(0, 10) : ''}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default React.memo(Tab2Details);
